import React, { useState } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import { faCalendar, faBolt, faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import VoxelDialogActionButton from '../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelPanel from '../../../components/voxel/Surfaces/VoxelPanel';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import { AppTierTypeGq } from '../../../graphql/projects/models/AppTierTypeGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';

export interface SetupSubscriptionButtonProps {
  team: CurrentTeamGq;
  user: CurrentUserGq;
}

export const SetupSubscriptionButton: React.FC<SetupSubscriptionButtonProps> = ({ user, team }) => {
  // Required licenses
  const requiredUserLicenses = 1;
  const requiredChildConnectionLicenses = Math.ceil(user.team.activeChildConnections / 3);
  const requiredLicenses = Math.max(requiredChildConnectionLicenses, requiredUserLicenses, 1);

  // Main button logic
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showLicenseWarning, setShowLicenseWarning] = useState(false);
  const handleAttemptCheckout = () => {
    const onTrial = [
      AppTierTypeGq.BETA_BASIC,
      AppTierTypeGq.BETA_CUSTOM,
      AppTierTypeGq.FREE_TRIAL,
    ].includes(user.team.appTier.tierType);
    const currentPeriodExpired =
      !user.team.activeSubscriptionPeriod ||
      moment.utc(user.team.activeSubscriptionPeriod.publicPeriodEnd).isBefore();

    if (onTrial && !currentPeriodExpired) {
      setShowCheckoutDialog(true);
      if (requiredLicenses > 1) {
        setShowLicenseWarning(true);
      }
    } else {
      handleCreateCheckoutSession(false);
    }
  };

  // Checkout mutation
  const stripeCreateCheckoutSession = usersApiService.mutations.useCreateStripeCheckoutSession();
  const [creatingCheckoutSession, setCreatingCheckoutSession] = useState(false);
  const handleCreateCheckoutSession = (startAfterTrial: boolean = false): Promise<unknown> => {
    setCreatingCheckoutSession(true);
    return stripeCreateCheckoutSession({ startAfterTrial })
      .then((result) => {
        window.location.href = result;
      })
      .catch((error) => {
        console.error(error);
        setCreatingCheckoutSession(false);
      })
      .finally(() => setShowCheckoutDialog(false));
  };
  return (
    <>
      {/* Button */}
      <div className="my-6 flex flex-col items-center">
        <VoxelButton
          startIcon={<FontAwesomeIcon icon={faCreditCard} />}
          color="primary"
          size="xl"
          title={`Setup subscription`}
          disabled={creatingCheckoutSession}
          onClick={handleAttemptCheckout}
        />
        {requiredLicenses > 1 && (
          <VoxelText variant="form-caption" className="mt-1">
            Includes {requiredLicenses} licenses
          </VoxelText>
        )}
      </div>

      {/* Dialog */}
      <VoxelDialog
        open={showCheckoutDialog}
        title="Setup subscription"
        onClose={() => setShowCheckoutDialog(false)}
      >
        {/* Dialog step 1 : License count warning */}
        {showLicenseWarning && (
          <>
            <VoxelDialogContent classMinWidth="w-[560px]">
              <VoxelAlert
                severity="info"
                widthClass="mt-2"
                title={`Includes ${requiredLicenses} licenses ($${99 + (requiredLicenses - 1) * 49
                  }/month)`}
              >
                Decrease the amount of team connections to reduce the initial license count.
              </VoxelAlert>
            </VoxelDialogContent>
            <VoxelDialogActions>
              <VoxelDialogActionCancel onClick={() => setShowCheckoutDialog(false)} />
              <VoxelDialogActionSubmit
                title="Continue"
                onClick={() => setShowLicenseWarning(false)}
              />
            </VoxelDialogActions>
          </>
        )}
        {/* Dialog step 2 : Start time */}
        {!showLicenseWarning && (
          <VoxelDialogContent classMinWidth="w-[560px]">
            <div className="grid grid-cols-2 w-full gap-4">
              <VoxelPanel
                className="flex flex-col gap-4 bg-gray-50"
                paddingClass="p-6"
                shadowClass="shadow"
              >
                <VoxelDialogActionButton
                  color="primary"
                  fullWidth
                  title="Start after trial"
                  onClick={() => handleCreateCheckoutSession(true)}
                  endIcon={<FontAwesomeIcon icon={faCalendar} />}
                  disabled={creatingCheckoutSession}
                />
                <VoxelText variant="form-caption">
                  The paid plan starts after your free trial ends in{' '}
                  {moment.utc(team.activeSubscriptionPeriod?.publicPeriodEnd).fromNow()}.
                </VoxelText>
              </VoxelPanel>
              <VoxelPanel
                className="flex flex-col gap-4 bg-gray-50"
                paddingClass="p-6"
                shadowClass="shadow"
              >
                {' '}
                <VoxelDialogActionButton
                  color="primary"
                  fullWidth
                  title="Start now"
                  onClick={() => handleCreateCheckoutSession(false)}
                  endIcon={<FontAwesomeIcon icon={faBolt} />}
                  disabled={creatingCheckoutSession}
                />
                <VoxelText variant="form-caption">
                  The paid plan starts now and you immediately get new capacity. Increase capacity,
                  users and connected teams further by adding additional licenses.
                </VoxelText>
              </VoxelPanel>
            </div>
          </VoxelDialogContent>
        )}
      </VoxelDialog>
    </>
  );
};

export default SetupSubscriptionButton;
